import { CommonModule } from '@angular/common';
import { ChartThemeService } from '../../../services/chart-theme.service';
import { Component, ElementRef, OnInit, OnDestroy, inject } from '@angular/core';
import { registerConfirmSteps, registerScreenActions, registerScreenPanels } from '../../../studio-nav/screen-panels.service';
import { ConfirmWaitsNgService } from '../../../services/mcp/actions/confirm-waits-ng.service';
import type { Prefilled } from '../../../services/mcp/actions/confirm-lane';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Firestore, doc, setDoc, getDoc } from '@angular/fire/firestore';
import { compressToEncodedURIComponent } from 'lz-string';
import { ClientData } from '../../../model/client.model';
import { AUDIT_ENTITY, ROLE_LOCAL_ENTITY } from '../../../model/entity.model';
import { buildDefaultPage, buildSandboxedLandingPage, buildSandboxedMobileLandingPage } from '../../../model/page-defaults';
import { AuthService } from '../../../services/auth.service';
import { ClientService } from '../../../services/client.service';
import { RoleService } from '../../../services/role.service';
import { MembersService, ProjectsLoadError } from '../../../services/members.service';
import { FunctionService } from '../../../services/function.service';
import { environment } from '../../../../environments/environment';
import { developerModeLabel } from '../../../services/dev-editor.util';
import { PROJECT_KEY } from '../../../studio-nav/studio-nav.service';
import { getZavoBridge } from '../../../services/mcp/zavo-bridge';
// import { OlympusAdminService } from '../../../services/olympus-admin.service';

@Component({
    selector: 'app-client-list',
    imports: [FormsModule, CommonModule],
    templateUrl: './client-list.component.html',
    styleUrl: './client-list.component.scss',
})
export class ClientListComponent implements OnInit, OnDestroy {
  private chartThemes = inject(ChartThemeService);

  private firestore = inject(Firestore);
  private hexBlinkTimer: any;
  createNewClientFlag = false;
  private panels = registerScreenPanels({
    screen: 'select-project',
    loading: () => this.projectsLoading,
    here: () => (this.createNewClientFlag ? { panel: 'create-project' } : {}),
    holds: () => ({
      // Left out after a failed load, so read_screen says not read instead of an empty list.
      ...(this.projectsLoadError ? {} : { projects: this.clientList.map((c) => ({ id: c.id, name: c.name })) }),
      searchTerm: this.searchTerm,
      newProjectName: this.newClient.name,
    }),
    select: () => {
      this.createNewClientFlag = true;
      return true;
    },
    unsaved: () => (this.createNewClientFlag && this.newClient.name.trim() ? { count: 1, name: 'The new project' } : null),
  });
  private offers = registerScreenActions({
    'search-projects': async (values) => {
      this.searchTerm = String(values['searchTerm'] ?? '').trim();
      this.filterClients();
      return { done: this.searchTerm ? `Searched projects for ${this.searchTerm}.` : 'Cleared the project search.', result: { searchTerm: this.searchTerm, shown: this.filteredClients.length } };
    },
    'reload-list': async () => {
      await this.loadMyProjects();
      if (this.projectsLoadError) throw new Error('The builder\'s projects could not be loaded. The picker says so and offers Try again.');
      return { done: 'Reloaded the projects.', result: { shown: this.filteredClients.length } };
    },
  });
  private waits = inject(ConfirmWaitsNgService);
  private confirmSteps = registerConfirmSteps({ 'create-project': { press: (values) => this.prefillCreate(values) } });
  newClient: ClientData = {
    name: '',
    id: '',
  };
  clientList: ClientData[] = [];
  clientDataList: any[] = [];
  index = 0;
  searchTerm: string = '';
  filteredClients: any[] = [];
  // Builder project-access filter (Track B): the picker shows ONLY projects the builder is a member
  // of (zavoConfig.project_members), not every client. null = membership not loaded yet → show none
  // (avoid flashing all clients). memberClientList = clientDataList ∩ myProjectIds; search runs on it.
  private myProjectIds: Set<string> | null = null;
  private memberClientList: any[] = [];
  projectsLoading = true;
  projectsLoadError: ProjectsLoadError['kind'] | null = null;
  developerModeByProject = new Map<string, string>();
  developerModeLabel = developerModeLabel;

  // Property to track info card expanded state
  showInfoDetails = false;

  // Track migration loading state
  migrationLoadingStates: { [key: string]: boolean } = {};

  // Honeycomb background grid
  hexRows: number[][] = [];

  constructor(
    private authService: AuthService,
    public clientService: ClientService,
    private roleService: RoleService,
    private router: Router,
    private functionService: FunctionService,
    private membersService: MembersService,
    private el: ElementRef
  ) {
    this.clientService.$values.subscribe((value: any) => {
      this.clientList = value;
      this.clientDataList = this.convertToDataList(value);
      this.recomputeVisible();
    });
    this.buildHexGrid();
  }

  ngOnInit() {
    this.startHexBlink();
    this.loadMyProjects();
    window.addEventListener('online', this.onBackOnline);
  }

  /** Load the builder's project memberships → restrict the picker to their own projects.
   *  A failed load is shown as a failure (projectsLoadError), never as "No projects yet" (zavo_oct_04). */
  loadMyProjects(): Promise<void> {
    this.projectsLoading = true;
    this.projectsLoadError = null;
    return this.membersService.listMyProjects()
      .then(async (ps) => {
        this.myProjectIds = new Set(ps.map((p) => p.projectId));
        this.developerModeByProject = new Map(
          ps.filter((p) => p.developerMode?.on).map((p) => [p.projectId, developerModeLabel(p.developerMode)!] as [string, string]));
        // The list's own read fails silently and never emits, which would read as "No projects yet" to someone who has some.
        // Was (2026-10-30), when an empty answer counted as failed too, so memberships whose projects are gone failed for good: !(await this.clientService.readMine())?.length
        if (ps.length && !this.clientList?.length && (await this.clientService.readMine()) === undefined) throw new ProjectsLoadError('server');
      })
      .catch((e) => {
        this.myProjectIds = null;
        this.projectsLoadError = e instanceof ProjectsLoadError ? e.kind : 'server';
      })
      .finally(() => { this.projectsLoading = false; this.recomputeVisible(); });
  }

  /** The connection came back while the picker was showing a network failure — try again by itself. */
  private onBackOnline = () => {
    if (this.projectsLoadError === 'network') this.loadMyProjects();
  };

  /** clientDataList ∩ membership → memberClientList, then apply the current search term. */
  private recomputeVisible(): void {
    this.memberClientList = this.myProjectIds
      ? this.clientDataList.filter((c) => this.myProjectIds!.has(c[0]))
      : []; // membership not loaded yet — show none rather than leak every client
    this.filterClients();
  }

  ngOnDestroy() {
    clearInterval(this.hexBlinkTimer);
    window.removeEventListener('online', this.onBackOnline);
  }

  /** Sign out and return to the login page (root route '' — there is no '/login' route). */
  signOut() {
    this.authService.signOut().then(() => {
      this.router.navigate(['/']);
    }).catch(error => {
      console.error('Error signing out:', error);
    });
  }

  private startHexBlink() {
    const colors = ['rgba(249,115,22,0.35)', 'rgba(168,85,247,0.3)', 'rgba(251,146,60,0.3)', 'rgba(192,132,252,0.25)'];
    const rows = this.hexRows.length;
    const cols = this.hexRows[0]?.length || 0;

    const getNeighbors = (r: number, c: number): [number, number][] => {
      const even = r % 2 === 0;
      return [
        [r - 1, even ? c - 1 : c], [r - 1, even ? c : c + 1],
        [r, c - 1], [r, c + 1],
        [r + 1, even ? c - 1 : c], [r + 1, even ? c : c + 1],
      ].filter(([nr, nc]) => nr >= 0 && nr < rows && nc >= 0 && nc < cols) as [number, number][];
    };

    const blinkCell = (r: number, c: number, color: string, fast: boolean) => {
      const cell = this.el.nativeElement.querySelector(`[data-hex="${r}-${c}"]`) as SVGPathElement;
      if (!cell) return;
      cell.setAttribute('stroke', color);
      cell.classList.remove('hex-blink', 'hex-blink-fast');
      void cell.getBoundingClientRect();
      cell.classList.add(fast ? 'hex-blink-fast' : 'hex-blink');
      cell.addEventListener('animationend', () => cell.classList.remove('hex-blink', 'hex-blink-fast'), { once: true });
    };

    this.hexBlinkTimer = setInterval(() => {
      if (!rows || !cols) return;
      const clusterCount = 1 + Math.floor(Math.random() * 3);
      for (let g = 0; g < clusterCount; g++) {
        const seedR = Math.floor(Math.random() * rows);
        const seedC = Math.floor(Math.random() * cols);
        const color = colors[Math.floor(Math.random() * colors.length)];
        blinkCell(seedR, seedC, color, false);
        const neighbors = getNeighbors(seedR, seedC);
        const spread = 2 + Math.floor(Math.random() * 4);
        for (let i = 0; i < Math.min(spread, neighbors.length); i++) {
          const pick = Math.floor(Math.random() * neighbors.length);
          const [nr, nc] = neighbors.splice(pick, 1)[0];
          setTimeout(() => blinkCell(nr, nc, color, true), 80 + Math.random() * 250);
        }
      }
    }, 600);
  }

  buildHexGrid() {
    const cols = Math.ceil(window.innerWidth / 110) + 2;
    const rows = Math.ceil(window.innerHeight / 94) + 4;
    this.hexRows = Array.from({ length: rows }, () =>
      Array.from({ length: cols }, () => 0)
    );
  }

  hexDelay(row: number, col: number): string {
    return (row * 0.3 + col * 0.15) + 's';
  }

  hexColor(row: number, col: number): string {
    return (row + col) % 2 === 0
      ? 'rgba(249,115,22,0.45)'
      : 'rgba(168,85,247,0.4)';
  }

  private identiconCache: { [key: string]: string[] } = {};
  getIdenticon(name: string): string[] {
    if (this.identiconCache[name]) return this.identiconCache[name];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
    }
    const half: boolean[] = [];
    for (let i = 0; i < 8; i++) {
      half.push(((hash >> i) & 1) === 1);
    }
    // Ensure at least 5 of 8 half-cells are on
    let onCount = half.filter(Boolean).length;
    let flipHash = Math.abs(hash >> 16);
    while (onCount < 5) {
      const idx = flipHash % 8;
      if (!half[idx]) { half[idx] = true; onCount++; }
      flipHash = (flipHash >> 2) + 1;
    }
    const grid = [
      half[0], half[1], half[1], half[0],
      half[2], half[3], half[3], half[2],
      half[4], half[5], half[5], half[4],
      half[6], half[7], half[7], half[6],
    ];
    const cells: string[] = grid.map(on => on ? 'white' : 'off');
    const onIndices: number[] = [];
    cells.forEach((c, i) => { if (c === 'white') onIndices.push(i); });
    const colorCount = 2 + (Math.abs(hash >> 10) % 3);
    let colorHash = Math.abs(hash >> 14);
    for (let c = 0; c < colorCount && onIndices.length > 0; c++) {
      const pick = colorHash % onIndices.length;
      cells[onIndices[pick]] = c % 2 === 0 ? 'orange' : 'purple';
      onIndices.splice(pick, 1);
      colorHash = (colorHash >> 3) | (colorHash << 29);
    }
    this.identiconCache[name] = cells;
    return cells;
  }

  hexRowOffset(row: number): string {
    const x = row % 2 !== 0 ? 54 : 0;
    const y = -row * 28;
    return `translate(${x}px, ${y}px)`;
  }
  convertToDataList(data: any) {
    var array: any[] = [];
    for (let i = 0; i < data.length; i++) {
      array.push([data[i].id, data[i].name, data[i].id]);
    }
    return array;
  }
  createNewClientFlagChange() {
    this.createNewClientFlag = !this.createNewClientFlag;
    if (!this.createNewClientFlag) this.waits.report('create-project', { outcome: 'discarded' });
  }
  private async prefillCreate(values: Record<string, unknown>): Promise<Prefilled> {
    const name = String(values['newProjectName'] ?? '').trim();
    if (!name) return { refused: 'A new project needs a name. Nothing was changed.' };
    if (this.panels.unsaved?.()) return { refused: 'The builder has the new project form open with a name in it. Nothing was changed. Ask them to finish or close it, then try again.' };
    this.newClient.name = name;
    this.createNewClientFlag = true;
    return {};
  }
  async createNewClient() {
    this.index++;
    this.waits.saving('create-project');
    this.newClient.id = await this.generateClientId();
    if (this.checkUniqueId()) {
      try {
        // Create the client first (without roles array in the document)
        await this.clientService.create(this.newClient);

        // NOTE: we deliberately do NOT seed a default 'admin' role here. The reserved 'admin' role is
        // SYNTHESIZED on every manageRole.listRoles read and the gateway grants it full access purely
        // from the JWT claim roleId==='admin' — no stored zavoConfig.roles row is required (proven:
        // projects with zero stored roles still have a working admin). The old seed created
        // `${clientId}_admin` rows via RoleService, which keys writes off the *currently-selected*
        // project (not the one being created) — so every seed landed under the wrong project_id and
        // was a no-role, no-op duplicate of the synthesized admin anyway. Builders create real business
        // roles from the owner console (Roles section) with plain string ids. See
        // docs/plans/roles-management-implementation.md.

        // Seed the default data every client starts with (pages, global
        // variables, audit entity). This used to be done lazily in the
        // services (PageService/GlobalVariablesService/EntityService) the
        // first time the client was opened with empty data — provisioning it
        // here at creation time is the correct place for it.
        await this.seedClientDefaults(this.newClient.id);

        // ─── THE CHART CONFIG IS COPIED, NOT INHERITED ─────────────────────────────────────────
        // A project takes a SNAPSHOT of the builder's default at creation and detaches immediately:
        // a builder changing their global preference afterwards must never restyle a project already
        // underway — the guarantee publish-freeze gives running apps, applied one step earlier.
        //
        // ⚠ THE ID IS PASSED EXPLICITLY. The new project is not the selected one yet, and anything
        // keyed off the selected path would write this onto whichever project happens to be open —
        // the same mistake the role seeding above documents.
        //
        // Failure is not fatal: the project simply falls back to the lazy path, which is what every
        // project created before today already does.
        try {
          await this.chartThemes.saveProjectConfig(
            this.newClient.id, await this.chartThemes.loadBuilderPrefs());
        } catch (e) {
          console.error('chart config copy failed; project will follow the builder default:', e);
        }

        // Claim OWNER on the new project in zavoConfig.project_members. Without this the project has
        // no membership row and the membership-filtered picker hides it (it gets created but never
        // shows). Then add it to the local membership set + recompute so it appears immediately.
        const claim = await this.membersService.createProject(this.newClient.id);
        if (!claim.ok) console.error('createProject (ownership claim) failed:', claim.error);
        this.myProjectIds = this.myProjectIds || new Set<string>();
        this.myProjectIds.add(this.newClient.id);
        this.recomputeVisible();

        // Provision tenancy in the background: the first sync creates the tables (systemOotbUser/Tenant)
        // and — via syncEntities' folded provisionProject — the project's Default (Editor) tenant, so the
        // project is gateway-ready with no manual Sync. Fire-and-forget (the builder can also Sync manually).
        this.triggerInitialSync(this.newClient.id);

        // await this.adminService.createSchema(this.newClient.id);
        // Reported first: closing the form by itself reads as the builder walking away.
        this.waits.report('create-project', {
          outcome: 'saved',
          saved: {
            id: this.newClient.id,
            name: this.newClient.name,
            ...(claim.ok ? {} : { warning: `The project was created, but the builder wasn't made its owner: ${claim.error ?? 'the claim failed'}.` }),
          },
        });
        this.createNewClientFlag = !this.createNewClientFlag;
      } catch (error) {
        this.waits.report('create-project', { outcome: 'failed', reason: 'Client Not Created: ' + error });
        alert('Client Not Created: ' + error);
      }
    } else {
      this.waits.report('create-project', { outcome: 'failed', reason: 'try again: unique id is already present' });
      alert('try again: unique id is already present');
    }
  }
  /**
   * Seeds the default data a brand-new client starts with. Written directly to
   * Firestore under the new client's path (rather than via the repository
   * services, whose collectionPath is bound to the currently-selected client).
   *
   * Note: user_global / role_global entities are intentionally NOT seeded here —
   * those are owned by the user-management flow.
   */
  private async seedClientDefaults(clientId: string): Promise<void> {
    const basePath = 'client/' + clientId;

    // Default pages. Pages are stored compressed under a single `blobData`
    // field — this mirrors BaseRepositoryService.create()'s `/pages` branch.
    const defaultPages = [
      buildDefaultPage('web', 'defaultPage'),
      buildDefaultPage('mobile', 'defaultMobilePage'),
      // The two public landing pages (signin + signup) — undeletable + sandboxed toolbox. Web and
      // mobile are seeded together so a new project never has one without the other.
      buildSandboxedLandingPage(),
      buildSandboxedMobileLandingPage(),
    ];
    for (const page of defaultPages) {
      await setDoc(doc(this.firestore, `${basePath}/pages`, page.id), {
        blobData: compressToEncodedURIComponent(JSON.stringify(page)),
      });
    }

    // Single global-variable document.
    await setDoc(doc(this.firestore, `${basePath}/globalVaraible`, 'globalVariable'), {
      id: 'globalVariable',
    });

    // Audit system entity.
    await setDoc(doc(this.firestore, `${basePath}/entity`, AUDIT_ENTITY.id), AUDIT_ENTITY);

    // Local "role" entity — seeded from a constant (like audit), NOT the OOTB picker. It's the
    // get_roles_list output shape; roles actually live in zavoConfig via manageRole, so it's a LOCAL
    // display container: never a synced table, never an OOTB import.
    await setDoc(doc(this.firestore, `${basePath}/entity`, ROLE_LOCAL_ENTITY.id), ROLE_LOCAL_ENTITY);

    // System OOTB identity entities every project needs so the user/tenant blocks work out of the box:
    //   systemOotbUser         — visible, synced (user management is a real app feature)
    //   systemOotbTenant       — hidden control table (tenant registry)
    //   systemOotbTenantInvite — VISIBLE (tenant-invite-flow): builders bind a table to it to show
    //                            pending-invite status. Its accept `token` is a CF-owned column, not a
    //                            field, and is excluded from every app-role SELECT — so nothing to hide.
    // Copied from the OOTB templates so the exact ids the blocks + gateway key on are preserved.
    // hidden is force-set here only for systemOotbTenant (the registry stays out of the UI).
    // Best-effort: a hiccup must NEVER fail project creation.
    //
    // status is ALSO force-set, and must be. The templates carry status 'NEW', which is correct for
    // the picker flow (a builder picks an OOTB entity, designs it, then syncs) but wrong here:
    // syncEntities drops NEW outright — `filter(e => e.status !== 'NEW' && e.status !== 'LOCAL')` —
    // so a verbatim copy produces an entity sync ignores FOREVER. The project then has no
    // systemOotbTenant table, provisionProject can't create the Default tenant, the Track-D tenant_id
    // backfill skips, and every sync still returns ok:true. Silent half-provisioning; the builder
    // only finds out when queries fail. These three are auto-provisioned infrastructure, not drafts,
    // so they go in ready to sync. (Fixed 2026-08-09 after it stranded NTk1Nz and NjAyMD.)
    const HIDDEN_IDENTITY = new Set(['systemOotbTenant']);
    for (const oid of ['systemOotbUser', 'systemOotbTenant', 'systemOotbTenantInvite']) {
      try {
        const src = await getDoc(doc(this.firestore, 'ootbEntity', oid));
        if (!src.exists()) { console.warn(`seedClientDefaults: OOTB template ${oid} missing — skipped`); continue; }
        const data: any = src.data();
        if (HIDDEN_IDENTITY.has(oid)) data.hidden = true;
        data.status = 'READY';
        await setDoc(doc(this.firestore, `${basePath}/entity`, oid), data);
      } catch (e) {
        console.error(`seedClientDefaults: failed to auto-import ${oid}`, e);
      }
    }
  }

  /**
   * Kick off the first sync for a brand-new project (background, fire-and-forget). That sync creates
   * its Postgres tables and — via provisionProject folded into syncEntities — its "Default (Editor)"
   * tenant, so builder entity queries don't 403 with "Project has no default tenant". Non-blocking:
   * failures are logged and the builder can always Sync manually.
   */
  private async triggerInitialSync(clientId: string): Promise<void> {
    try {
      const token = await this.authService.getAuthToken();
      if (!token) return;
      await fetch(`${environment.functionsBaseUrl}/syncEntities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ clientId }),
      });
    } catch (e) {
      console.error('initial project sync failed — the builder can Sync manually', e);
    }
  }

  checkUniqueId() {
    for (let i = 0; i < this.clientList.length; i++) {
      if (this.clientList[i].id == this.newClient.id) {
        return false;
      }
    }
    return true;
  }
  generateClientId(): string {
    const randomInt = Math.floor(Math.random() * 10000000000000);
    const timestamp = Date.now();
    const combinedValue = randomInt + timestamp; // Combine random number and timestamp
    const base64String = btoa(String(combinedValue));
    return base64String.substring(0, 6); // Extract first 6 characters
  }

  // slection of client
  selectedClient() {
    for (let i = 0; i < this.clientList.length; i++) {
      if (this.clientList[i].id == this.clientService.selectedClientId) {
        this.clientService.selectedClient = this.clientList[i];
        this.authService.baseCollectionPath =
          'client/' + this.clientService.selectedClientId;
        // Nav 7b final review (I8) — a tenant belongs to the project it was chosen in; the next one starts on its default.
        this.clientService.selectedTenantId = null;
        this.authService.clientChanged$.next(this.clientService.selectedClientId);
        // Nav 7b (spec Q8, R4) — a manual project choice is remembered, so a reload stays in it.
        sessionStorage.setItem(PROJECT_KEY, this.clientService.selectedClientId);
        this.router.navigate(['/dashboard/entity-list']);
        break;
      }
    }
  }

  filterClients(): void {
    // Search runs over the membership-filtered set (memberClientList), never the full client list.
    if (!this.searchTerm.trim()) {
      this.filteredClients = [...this.memberClientList];
      return;
    }

    const term = this.searchTerm.toLowerCase();
    this.filteredClients = this.memberClientList.filter(client =>
      client[1].toLowerCase().includes(term) ||
      client[2].toLowerCase().includes(term)
    );
  }
  
  selectClientFromList(id: string): void {
    this.clientService.selectedClientId = id;
  }

  /**
   * Chart style is a BUILDER preference, so this navigates without selecting a client.
   *
   * Deliberately not `selectedClient()` first: that sets `baseCollectionPath`, fires
   * `clientChanged$` and routes to the entity list, none of which a builder-level settings screen
   * wants. Back from there returns here.
   */
  openChartStyle(): void {
    this.router.navigate(['/chart-style']);
  }

  /** Only the Zavo desktop app can connect Claude; its bridge opens the app's own panel, which holds the token. */
  readonly canConnectClaude = typeof getZavoBridge()?.openConnectPanel === 'function';

  /** No project is open here yet, so the panel says to open one next; Claude's tools work inside a project. */
  openConnectClaude(): void {
    getZavoBridge()?.openConnectPanel?.('no-project');
  }

  /**
   * Toggles the visibility of the info card details
   */
  toggleInfoCard(): void {
    this.showInfoDetails = !this.showInfoDetails;
  }

  // DEPRECATED: Migrate to Prod (copyClient) is no longer supported. Commented out for now.
  // /**
  //  * Checks if a client can be migrated to production
  //  * @param clientId The client ID to check
  //  * @returns true if the client can be migrated to prod
  //  */
  // canMigrateToProd(clientId: string): boolean {
  //   // Check if the ID already ends with "prod"
  //   if (clientId.endsWith('_prod')) {
  //     return false;
  //   }
  //
  //   return true;
  // }

  // /**
  //  * Migrates a client to production by creating a new client with "_prod" suffix
  //  * @param sourceClientId The source client ID to migrate
  //  */
  // async migrateToProd(sourceClientId: string): Promise<void> {
  //   const sourceClient = this.clientList.find(client => client.id === sourceClientId);
  //   if (!sourceClient) {
  //     alert('Source client not found');
  //     return;
  //   }
  //
  //   const id = sourceClientId + '_prod';
  //
  //   // Set loading state
  //   this.migrationLoadingStates[sourceClientId] = true;
  //
  //   try {
  //     await this.functionService.call("copyClient", {clientId: sourceClientId, newClientId: id});
  //     alert(`✅ Client "${sourceClient.name}" successfully migrated to production!`);
  //   } catch (error) {
  //     console.error('Migration failed:', error);
  //     alert(`❌ Migration failed: ${error || 'Unknown error occurred'}`);
  //   } finally {
  //     // Clear loading state
  //     this.migrationLoadingStates[sourceClientId] = false;
  //   }
  // }
}
